import { useCallback, useState } from 'react';
import AddCircleOutline from '@mui/icons-material/AddCircleOutline';
import EditIcon from '@mui/icons-material/Edit';
import { Box, Grid, IconButton, List, styled, Typography } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { useTransferCompanyMember, useUpdateCompany } from '../../../../hooks/useCompany';
import { useGetUser } from '../../../../hooks/useUser';
import { useDialogStore } from '../../../../stores/dialogStore';
import AddUserDialog from './AddUserDialog';
import TransferUserCompanyDialog from './TransferUserCompanyDialog';
import UpdateOwnerDialog from './UpdateOwnerDialog';
import UserInfoCard from './UserInfoCard';

const CompanyInformationData = ({ companyData }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const openDialog = useDialogStore((state) => state.openDialog);

  // Update owner
  const [selectedUser, setSelectedUser] = useState(null);
  const [updateOwnerDialog, setUpdateOwnerDialog] = useState(false);

  // Add user
  const [userId, setUserId] = useState('');
  const [addNewUserDialog, setAddNewUserDialog] = useState(false);

  // Transfer user
  const [transferUserDialog, setTransferUserDialog] = useState(false);
  const [transferCompanyCode, setTransferCompanyCode] = useState('');

  const { data: userListData, isPending: userListLoading } = useGetUser({
    company_id: companyData?._id || undefined,
  });
  const userList = userListData?.Data.data || [];

  const { mutateAsync: updateCompany } = useUpdateCompany();
  const { mutateAsync: transferCompany } = useTransferCompanyMember();

  const handleUpdateOwner = (user) => {
    setUpdateOwnerDialog(true);
    setSelectedUser({
      _id: user._id,
      displayName: user.displayName,
    });
  };

  const handleSubmitUpdateOwner = async () => {
    try {
      const response = await updateCompany({
        params: { id: companyData?._id },
        dto: { user_id: selectedUser._id },
      });
      if (response.Code === '20100') {
        openDialog({
          title: 'สำเร็จ',
          message: 'อัพเดตสิทธิ์การแลกรางวัลสำเร็จ',
          variant: 'success',
          onCloseCallback: () => {
            queryClient.invalidateQueries({ queryKey: ['company-list'] });
          },
        });
      }
    } catch (e) {
      console.error(e);
      openDialog({
        title: 'เกิดข้อผิดพลาด',
        message: 'อัพเดตสิทธิ์การแลกรางวัลไม่สำเร็จ',
        variant: 'error',
      });
    } finally {
      setUpdateOwnerDialog(false);
    }
  };

  const handleCompanyTransferMember = (data) => {
    setTransferUserDialog(true);
    setUserId(data._id);
  };

  const handleConfirmCompanyTransferMember = useCallback(async () => {
    try {
      if (transferCompanyCode && userId) {
        const response = await transferCompany({
          dto: {
            code_company_destination: transferCompanyCode,
            user_id_origin: userId,
          },
        });

        if (response.Code === '20100') {
          openDialog({
            title: 'สำเร็จ',
            message: 'ย้ายบริษัทสำเร็จ',
            variant: 'success',
            onCloseCallback: () => {
              queryClient.invalidateQueries({ queryKey: ['userList'] });
              setTransferUserDialog(false);
              setTransferCompanyCode('');
            },
          });
        }
      }
    } catch (err) {
      console.error(err);
      openDialog({
        title: 'เกิดข้อผิดพลาด',
        message: err.response?.data?.Message || 'ย้ายบริษัทไม่สำเร็จ',
        variant: 'error',
      });
    }
  }, [transferCompanyCode, userId, openDialog, queryClient, transferCompany]);

  return (
    <>
      <Grid
        container
        columnSpacing={2}
        rowSpacing={1}
        sx={{ padding: '20px 8px', position: 'relative' }}
      >
        {/* Edit */}
        <Box sx={{ position: 'absolute', top: '10px', right: '10px' }}>
          <EditIcon
            sx={{ fontSize: '1.5rem', color: '#002B38', cursor: 'pointer' }}
            onClick={() => navigate(`/admin/company/${companyData?._id}`)}
          />
        </Box>
        {/* Company code */}
        <Grid item xs={5} sm={4}>
          <TopicTypo>รหัสบริษัท:</TopicTypo>
        </Grid>
        <Grid item xs={7} sm={8}>
          <ValueTypo>{companyData?.code}</ValueTypo>
        </Grid>

        {/* Company name */}
        <Grid item xs={5} sm={4}>
          <TopicTypo>ชื่อบริษัท:</TopicTypo>
        </Grid>
        <Grid item xs={7} sm={8}>
          <ValueTypo>{companyData?.company_name}</ValueTypo>
        </Grid>

        {/* Status */}
        <Grid item xs={5} sm={4}>
          <TopicTypo>สถานะ:</TopicTypo>
        </Grid>
        <Grid item xs={7} sm={8}>
          <ValueTypo>{companyData?.state}</ValueTypo>
        </Grid>

        {/* Accumulate point */}
        <Grid item xs={5} sm={4}>
          <TopicTypo>คะแนนสะสม:</TopicTypo>
        </Grid>
        <Grid item xs={7} sm={8}>
          <ValueTypo>{companyData?.accumulated_points?.toFixed(2)}</ValueTypo>
        </Grid>

        {/* Criteria point */}
        <Grid item xs={5} sm={4}>
          <TopicTypo>เกณฑ์การให้คะแนน:</TopicTypo>
        </Grid>
        <Grid item xs={7} sm={8}>
          <ValueTypo>{companyData?.spending_criteria}</ValueTypo>
        </Grid>

        {/* Spending amount */}
        <Grid item xs={5} sm={4}>
          <TopicTypo>ยอดที่ใช้ไปล่าสุด:</TopicTypo>
        </Grid>
        <Grid item xs={7} sm={8}>
          <ValueTypo>{companyData?.spending_amount?.toFixed(2)}</ValueTypo>
        </Grid>

        {/* People name */}
        <Grid item xs={5} sm={4}>
          <TopicTypo>ชื่อผู้มีอำนาจ:</TopicTypo>
        </Grid>
        <Grid item xs={7} sm={8}>
          <ValueTypo>{companyData?.people_name}</ValueTypo>
        </Grid>

        {/* List user */}
        <Grid item xs={12} sm={4}>
          <TopicTypo>รายชื่อผู้ใช้งาน:</TopicTypo>
        </Grid>
        <Grid item xs={12} sm={8}>
          <List sx={{ width: '100%', bgcolor: 'background.paper' }}>
            {!userListLoading && userList.length > 0
              ? userList.map((item) => {
                  return (
                    <UserInfoCard
                      key={item._id}
                      userId={item._id}
                      displayName={item.displayName}
                      pictureUrl={item.pictureUrl}
                      phonenumber={item.phonenumber}
                      isOwner={companyData?.user_id?._id === item._id}
                      onUpdateOwner={() => handleUpdateOwner(item)}
                      onTransferMember={() => handleCompanyTransferMember(item)}
                    />
                  );
                })
              : null}
          </List>
        </Grid>
        <Grid item xs={12} sm={4} sx={{ display: { xs: 'none', sm: 'block' } }} />
        <Grid item xs={12} sm={8}>
          <IconButton
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              borderRadius: '8px',
            }}
            onClick={() => setAddNewUserDialog(true)}
          >
            <AddCircleOutline sx={{ fontSize: '2.25rem', color: 'black' }} />
            <Typography sx={{ fontSize: '1.5rem', color: 'black' }}>เพิ่มสมาชิก</Typography>
          </IconButton>
        </Grid>
      </Grid>

      {/* Update owner */}
      <UpdateOwnerDialog
        updateOwnerDialog={updateOwnerDialog}
        selectedUser={selectedUser}
        handleCloseUpdateOwnerDialog={() => setUpdateOwnerDialog(false)}
        handleSubmitUpdateOwner={handleSubmitUpdateOwner}
      />
      {/* Transfer user */}
      <TransferUserCompanyDialog
        open={transferUserDialog}
        onClose={() => setTransferUserDialog(false)}
        value={transferCompanyCode}
        onChange={setTransferCompanyCode}
        onConfirm={handleConfirmCompanyTransferMember}
        companyCode={id}
      />
      {/* Add user */}
      <AddUserDialog
        open={addNewUserDialog}
        onClose={() => setAddNewUserDialog(false)}
        companyData={companyData}
      />
    </>
  );
};

const TopicTypo = styled(Typography)({
  fontSize: '1.5rem',
  fontWeight: '500',
  color: '#004B61',
  whiteSpace: 'pre-wrap',
});

const ValueTypo = styled(Typography)({
  fontSize: '1.5rem',
  color: '#004B61',
  whiteSpace: 'pre-wrap',
});

export default CompanyInformationData;
